import { Divider, Icon, ISelectOption, Select } from '@aon/aon-tws-common-ui';
import React from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { ISurveyDimension, ISurveyQuestionEditModel } from '../../../models';
import { isDimensionQuestionSelectable, isSurveyQuestionEngagementQuestion } from '../../../utilities/DimensionHelper';
import DimensionQuestionTable from './DimensionQuestionTable';

const bulkActions: ISelectOption[] = [
	{
		displayText: 'Move under another dimension or index',
		name: 'Move',
		displayOrder: 0,
	},
	{
		displayText: 'Copy to another dimension or index',
		name: 'Copy',
		displayOrder: 1,
	},
	{
		displayText: 'Remove from dimension',
		name: 'Remove',
		displayOrder: 2,
	},
];

const DimensionQuestionsContainer = styled.div`
	.tws-select-container {
		padding-right: 0px;
	}
`;

const WarningMessageContainer = styled.p`
	color: red;
	margin-top: 1em;
	padding-left: 10px;
	verticle-align: middle;

	& i {
		margin-right: 5px;
		vertical-align: text-bottom;
	}
`;

export interface IDimensionQuestionProps {
	questions: ISurveyQuestionEditModel[];
	dimension: ISurveyDimension;
	selectedQuestions: ISurveyQuestionEditModel[];
	onToggleSingleQuestionSelection: (dimension: ISurveyDimension, question: ISurveyQuestionEditModel, isSelected: boolean) => void;
	onToggleAllQuestionsSelection: (dimension: ISurveyDimension, questions: ISurveyQuestionEditModel[], isSelected: boolean) => void;
	onMoveQuestions: (sourceDimension: ISurveyDimension, questionsToMove: ISurveyQuestionEditModel[]) => void;
	onCopyQuestions: (sourceDimension: ISurveyDimension, questionsToCopy: ISurveyQuestionEditModel[]) => void;
	onRemoveQuestions: (sourceDimension: ISurveyDimension, questionsToRemove: ISurveyQuestionEditModel[]) => void;
}

const DimensionQuestions = ({
	questions,
	dimension,
	selectedQuestions,
	onToggleAllQuestionsSelection,
	onToggleSingleQuestionSelection,
	onMoveQuestions,
	onCopyQuestions,
	onRemoveQuestions,
}: IDimensionQuestionProps) => {
	const selectableQuestions = questions.filter((q) => isDimensionQuestionSelectable(q));

	const availableBulkActions =
		dimension.name === ContentConstants.DIMENSION_CATEGORIES_NO_DIMENSIONS
			? bulkActions.filter((ba) => ba.name !== bulkActions[2].name)
			: bulkActions;

	const handeBulkActionSelected = (selectedAction: string) => {
		if (selectedAction === bulkActions[0].name) {
			onMoveQuestions(dimension, selectedQuestions);
		} else if (selectedAction === bulkActions[1].name) {
			onCopyQuestions(dimension, selectedQuestions);
		} else if (selectedAction === bulkActions[2].name) {
			onRemoveQuestions(dimension, selectedQuestions);
		}
	};

	return (
		<DimensionQuestionsContainer>
			{!dimension.shouldDisableBulkActions && (
				<>
					<Select
						size='lg'
						selectSize='lg'
						dropdownSize='lg'
						items={availableBulkActions}
						displayText='Bulk Actions'
						multiple={false}
						showCount={false}
						disabled={selectedQuestions.length < 1}
						onApply={(value) => handeBulkActionSelected(value as string)}
					/>
					{questions.some((q) => isSurveyQuestionEngagementQuestion(q.code)) && (
						<WarningMessageContainer>
							<Icon name='error' />
							Engagement questions are restricted from moving to other dimension types
						</WarningMessageContainer>
					)}
					<Divider type='dashed' />
				</>
			)}
			<DimensionQuestionTable
				questions={questions}
				allSelected={selectableQuestions.length === selectedQuestions.length && selectableQuestions.length > 0}
				allowSelection={!dimension.shouldDisableBulkActions}
				selectedQuestions={selectedQuestions}
				onAllQuestionsSelected={(isSelected) => onToggleAllQuestionsSelection(dimension, questions, isSelected)}
				onSingleQuestionSelected={(selectedQuestion, isSelected) =>
					onToggleSingleQuestionSelection(dimension, selectedQuestion, isSelected)
				}
			/>
		</DimensionQuestionsContainer>
	);
};

export default DimensionQuestions;
